import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { localizePath } from '../utils/localizedRoutes.js';
import { AdminPageHead, AdminSection, ShowMore, useShowMore, writeAdminRoute } from '../components/AdminNav.jsx';
import { MemoryGraph, MemoryStores } from './CompanyMemoryGraph.jsx';
import { MemoryDay } from './CompanyMemoryDay.jsx';
import { formatBerlin } from './VeridionShared.jsx';

const API = '/api/admin/memory';
const formatTime = formatBerlin;

const Section = ({ id = '', title, subtitle, children, count = null, collapsed = false }) => (
  <AdminSection id={id} title={title} subtitle={subtitle} count={count} collapsed={collapsed}>{children}</AdminSection>
);

const Table = ({ columns, rows, empty }) => (
  <div className="admin-table-shell"><div className="admin-table-scroll">
    <table className="admin-table">
      <thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.id || index}>{columns.map((column) => <td key={column.key} className="break-words">{column.render ? column.render(row) : (row[column.key] ?? '-')}</td>)}</tr>
        ))}
        {!rows.length ? <tr><td colSpan={columns.length}>{empty}</td></tr> : null}
      </tbody>
    </table>
  </div></div>
);

const CompanyMemoryPage = ({ embedded = false, section = '' }) => {
  const { t, currentLang } = useTranslation();
  const isDe = currentLang === 'de';
  const m = t.admin.memory;
  const [allowed, setAllowed] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('');
  const [results, setResults] = useState(null);
  const [facts, setFacts] = useState([]);
  const [entityName, setEntityName] = useState('');
  const [entity, setEntity] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditAgent, setAuditAgent] = useState('');
  const [cypher, setCypher] = useState('MATCH (m:Memory) RETURN m.kind AS kind, m.scope AS scope, m.text AS text ORDER BY m.created_at DESC LIMIT 25');
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ text: '', kind: 'fact', scope: 'company' });
  const [notice, setNotice] = useState({ message: '', error: false });
  const [organisation, setOrganisation] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [domainsData, setDomainsData] = useState(null);
  const [auditShown, auditMore] = useShowMore(50);

  const fail = (failure) => setNotice({ message: `${m.failed} (${failure?.code || failure?.status || '-'})`, error: true });

  const load = useCallback(async () => {
    requestJson('/api/admin/veridion-core/organisation').then(setOrganisation).catch(() => setOrganisation(null));
    requestJson('/api/admin/veridion-core/memory-bubbles').then(setDomainsData).catch(() => setDomainsData(null));
    requestJson(`${API}/briefs`).then((data) => setBriefs(Array.isArray(data) ? data : data?.items || [])).catch(() => setBriefs([]));
    try {
      setOverview(await requestJson(`${API}/overview`));
      setError('');
    } catch (failure) {
      setError(failure?.code || 'unavailable');
    }
    requestJson(`${API}/facts?scope=${encodeURIComponent(scope)}`).then((data) => setFacts(data.items || [])).catch(() => {});
    requestJson(`${API}/audit?agent=${encodeURIComponent(auditAgent)}`).then((data) => setAudit(data.items || [])).catch(() => {});
  }, [scope, auditAgent]);

  useEffect(() => {
    fetchCurrentUser().then((current) => {
      const admin = Boolean(current.user?.roles?.includes('admin'));
      setAllowed(admin);
      if (admin) load();
    }).catch(() => setAllowed(false));
  }, [load]);

  if (allowed === null) return <div className="site-card p-6">{m.loading}</div>;
  if (!allowed) return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;

  const forget = async (id) => {
    if (!window.confirm(m.confirmForget)) return;
    try {
      await requestJson(`${API}/forget`, { method: 'POST', body: JSON.stringify({ id }) });
      setNotice({ message: m.forgotten, error: false });
      setResults((current) => (current ? current.filter((row) => row.id !== id) : current));
      load();
    } catch (failure) {
      fail(failure);
    }
  };
  const forgetColumn = { key: 'forget', label: '', render: (row) => (row.id ? <button type="button" className="homepage-button-secondary is-destructive" onClick={() => forget(row.id)}>{m.forget}</button> : '-') };

  const scopes = Array.from(new Set((overview?.memoryByScope || []).map((entry) => entry.scope).concat(['company'])));
  const show = (name) => !embedded || !section || section === name;

  // The memory-bubbles payload (D-VCORE-333 §5): seed-labeled domain rows with live Neo4j counts
  // (`counts_live` false means the graph was unreachable and the zeros are honest, not invented),
  // and the research archive as live `core.research_publications` rows. Every human-facing field
  // is bilingual through the backend's `_en` convention.
  const knowledgeDomains = domainsData?.bubbles || [];
  const researchRepo = domainsData?.quantum_research_space || {};
  const researchPapers = researchRepo?.papers || [];
  const pickField = (row, field) => (isDe ? row?.[field] : (row?.[`${field}_en`] || row?.[field]));

  const body = (
    <>
      <AdminPageHead
        title={m.title}
        subtitle={m.subtitle}
        meta={[
          [m.database, overview?.ready ? m.ready : m.notReady],
          [m.factExtraction, overview?.graphiti ? m.ready : m.notReady],
          [m.episodesQueued, overview?.episodesQueued ?? '-'],
          overview?.lastError ? [m.lastError, overview.lastError] : null,
        ]}
        actions={!embedded ? <a className="homepage-button-secondary" href={localizePath('/admin', currentLang)}>{t.nav?.admin}</a> : null}
      >
        <div className="homepage-muted-panel p-4 text-xs space-y-1 text-text-secondary">
          <div className="font-bold text-sm text-text-primary">{m.archOverviewTitle}</div>
          <div>{m.archOverviewText}</div>
        </div>
        {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{m.unavailable} ({error})</div> : null}
        {notice.message ? <div className={`homepage-muted-panel p-3 text-sm ${notice.error ? 'text-error' : ''}`}>{notice.message}</div> : null}
      </AdminPageHead>

      {show('day') ? <MemoryDay /> : null}

      {(show('bubbles') || show('content')) && domainsData ? (
        <>
          <Section
            id="memory-bu-domains"
            title={m.clustersTitle}
            subtitle={m.clustersSubtitle}
            count={knowledgeDomains.length}
          >
            <Table
              rows={knowledgeDomains}
              empty={m.empty}
              columns={[
                { key: 'bu_code', label: m.clusterCode, render: (row) => <span className="font-mono text-xs font-bold">{row.bu_code}</span> },
                { key: 'name', label: m.clusterName, render: (row) => (
                  <div>
                    <div className="font-bold">{isDe ? (row.german_name || row.name) : row.name}</div>
                    <div className="text-xs text-text-secondary">
                      {m.clusterId}: {row.id} · {m.clusterRoles}: {(row.lead_positions || []).join(', ')} · {m.clusterBridges}: {(row.cross_bubble_bridges || []).join(', ')}
                    </div>
                  </div>
                ) },
                { key: 'isolation_policy', label: m.clusterPolicy, render: (row) => pickField(row, 'isolation_policy') },
                { key: 'node_count', label: m.clusterCounts, render: (row) => (
                  row.counts_live === false
                    ? <span className="text-text-secondary">{m.countsUnavailable}</span>
                    : `${row.node_count} / ${row.edge_count}`
                ) },
                { key: 'recent_insights', label: m.clusterInsights, render: (row) => (
                  <ul className="list-disc pl-4 text-xs space-y-1">
                    {(pickField(row, 'recent_insights') || []).map((ins, idx) => <li key={idx}>{ins}</li>)}
                  </ul>
                ) },
                { key: 'open_graph', label: '', render: (row) => (
                  <button
                    type="button"
                    className="homepage-button-secondary text-xs"
                    onClick={() => writeAdminRoute('company-memory', 'graph', row.bu_code)}
                  >
                    {m.clusterOpenButton}
                  </button>
                ) },
              ]}
            />
          </Section>

          <Section
            id="memory-quantum-research-repository"
            title={pickField(researchRepo, 'title') || m.researchTitle}
            subtitle={m.researchSubtitle}
            count={researchPapers.length}
          >
            <Table
              rows={researchPapers.map((paper) => ({ ...paper, id: paper.pub_id }))}
              empty={m.researchEmpty}
              columns={[
                { key: 'title', label: m.researchPublication, render: (row) => (
                  <div>
                    <div className="font-bold">{isDe ? row.title : (row.title_en || row.title)}</div>
                    <div className="text-xs text-text-secondary">{[row.authors, row.venue, row.year].filter(Boolean).join(' · ')}</div>
                    {row.url || row.arxiv_id ? (
                      <div className="text-xs">
                        <a className="underline" href={row.url || `https://arxiv.org/abs/${row.arxiv_id}`} target="_blank" rel="noreferrer">
                          {row.arxiv_id || row.doi || row.url}
                        </a>
                      </div>
                    ) : null}
                  </div>
                ) },
                { key: 'key_mechanism', label: m.researchMechanism, render: (row) => pickField(row, 'key_mechanism') },
                { key: 'relevance', label: m.researchRelevance, render: (row) => pickField(row, 'relevance') },
                { key: 'origin', label: m.researchOrigin, render: (row) => (
                  <div className="text-xs">
                    {row.seeded ? <span className="admin-status-pill is-neutral">{m.researchSeeded}</span> : <span className="font-mono">{row.ingested_by}</span>}
                    {row.motivates_version ? <div className="text-text-secondary">{m.researchMotivates}: {row.motivates_version}</div> : null}
                  </div>
                ) },
              ]}
            />
            <p className="text-xs text-text-secondary">{m.researchIngestNote}</p>
          </Section>
        </>
      ) : null}

      {show('stores') ? <MemoryStores /> : null}

      {show('content') ? (
      <Section id="memory-content" title={m.contentTitle} subtitle={m.contentSubtitle}>
        <div className="grid gap-4 md:grid-cols-2">
          <Table columns={[{ key: 'label', label: m.label }, { key: 'count', label: m.count }]} rows={overview?.labels || []} empty={m.empty} />
          <Table columns={[{ key: 'scope', label: m.scope }, { key: 'kind', label: m.kind, render: (row) => m.kinds?.[row.kind] || row.kind }, { key: 'count', label: m.count }]} rows={overview?.memoryByScope || []} empty={m.empty} />
        </div>
      </Section>
      ) : null}

      {show('content') ? (
      <Section id="memory-organisation" title={t.admin.org.memoryTitle} subtitle={t.admin.org.memorySubtitle} collapsed>
        <Table rows={(organisation?.positions || []).map((position) => ({
          id: position.key,
          name: position.bot,
          role: position.title,
          area: position.area === 'company' ? 'company' : organisation.areas?.[position.area] || position.area,
          scope: position.key === 'coordination' ? 'company' : `position-${position.key}`,
        })).concat((organisation?.decisionMakers || []).map((maker) => ({ id: maker, name: maker, role: t.admin.org.decisionMaker, area: 'company', scope: 'company' })))}
          empty={m.empty}
          columns={[{ key: 'name', label: t.admin.org.memoryMember }, { key: 'role', label: t.admin.org.position }, { key: 'area', label: t.admin.org.area }, { key: 'scope', label: m.scope }]} />
      </Section>
      ) : null}

      {show('search') ? (
      <Section id="memory-briefs" title={m.briefsTitle} subtitle={m.briefsSubtitle} count={briefs.length} collapsed>
        <Table rows={briefs.map((brief) => ({ ...brief, id: brief.position }))} empty={m.noBriefs} columns={[
          { key: 'position', label: t.admin.org.position },
          { key: 'text', label: m.brief, render: (row) => <span style={{ whiteSpace: 'pre-wrap' }}>{row.text || '-'}</span> },
          { key: 'updated_at', label: m.updated, render: (row) => formatTime(row.updated_at) },
        ]} />
      </Section>
      ) : null}

      {show('graph') ? <MemoryGraph /> : null}

      {show('search') ? (
      <Section id="memory-search" title={m.searchTitle} subtitle={m.searchSubtitle}>
        <form className="flex flex-wrap gap-2" onSubmit={async (event) => { event.preventDefault(); try { setResults((await requestJson(`${API}/search?q=${encodeURIComponent(query)}&scope=${encodeURIComponent(scope)}`)).items || []); } catch (failure) { fail(failure); } }}>
          <input className="form-input flex-1 min-w-0" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={m.searchPlaceholder} required />
          <select className="form-input" value={scope} onChange={(event) => setScope(event.target.value)}>
            <option value="">{m.allScopes}</option>
            {scopes.map((entry) => <option key={entry} value={entry}>{entry}</option>)}
          </select>
          <button type="submit" className="homepage-button-primary">{m.search}</button>
        </form>
        {results ? (
          <Table rows={results} empty={m.noResults} columns={[
            { key: 'kind', label: m.kind, render: (row) => m.kinds?.[row.kind] || row.kind },
            { key: 'text', label: m.text },
            { key: 'scope', label: m.scope },
            { key: 'valid_at', label: m.validFrom, render: (row) => formatTime(row.valid_at) },
            { key: 'source', label: m.source, render: (row) => (row.source === 'graphiti' ? m.extracted : m.recorded) },
            forgetColumn,
          ]} />
        ) : null}
      </Section>
      ) : null}

      {show('remember') ? (
      <Section id="memory-remember" title={m.rememberTitle} subtitle={m.rememberSubtitle}>
        <form className="space-y-2" onSubmit={async (event) => { event.preventDefault(); try { await requestJson(`${API}/remember`, { method: 'POST', body: JSON.stringify(form) }); setForm({ ...form, text: '' }); setNotice({ message: m.remembered, error: false }); load(); } catch (failure) { fail(failure); } }}>
          <textarea className="form-input w-full" rows={3} value={form.text} onChange={(event) => setForm({ ...form, text: event.target.value })} required />
          <div className="flex flex-wrap gap-2">
            <select className="form-input" value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })}>
              {['fact', 'decision', 'task', 'procedure', 'note'].map((kind) => <option key={kind} value={kind}>{m.kinds?.[kind] || kind}</option>)}
            </select>
            <select className="form-input" value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value })}>
              {scopes.map((entry) => <option key={entry} value={entry}>{entry}</option>)}
            </select>
            <button type="submit" className="homepage-button-primary">{m.remember}</button>
          </div>
        </form>
        <Table rows={overview?.recent || []} empty={m.empty} columns={[
          { key: 'created_at', label: m.created, render: (row) => formatTime(row.created_at) },
          { key: 'kind', label: m.kind, render: (row) => m.kinds?.[row.kind] || row.kind },
          { key: 'text', label: m.text },
          { key: 'scope', label: m.scope },
          { key: 'invalid', label: m.validity, render: (row) => (row.invalid ? m.invalid : (
            <button type="button" className="homepage-button-secondary is-destructive" onClick={async () => { if (!window.confirm(m.confirmInvalidate)) return; try { await requestJson(`${API}/invalidate`, { method: 'POST', body: JSON.stringify({ id: row.id }) }); load(); } catch (failure) { fail(failure); } }}>{m.invalidate}</button>
          )) },
          forgetColumn,
        ]} />
      </Section>
      ) : null}

      {show('facts') ? (
      <Section id="memory-facts" title={m.factsTitle} subtitle={m.factsSubtitle} count={facts.length}>
        <Table rows={facts} empty={m.noFacts} columns={[
          { key: 'fact', label: m.fact },
          { key: 'source', label: m.from },
          { key: 'target', label: m.to },
          { key: 'scope', label: m.scope },
          { key: 'valid_at', label: m.validFrom, render: (row) => formatTime(row.valid_at) },
          { key: 'invalid_at', label: m.validUntil, render: (row) => (row.invalid_at && row.invalid_at !== 'None' ? formatTime(row.invalid_at) : m.stillValid) },
          forgetColumn,
        ]} />
      </Section>
      ) : null}

      {show('entity') ? (
      <Section id="memory-entity" title={m.entityTitle} subtitle={m.entitySubtitle}>
        <form className="flex flex-wrap gap-2" onSubmit={async (event) => { event.preventDefault(); try { setEntity(await requestJson(`${API}/entity?name=${encodeURIComponent(entityName)}`)); } catch (failure) { fail(failure); } }}>
          <input className="form-input flex-1 min-w-0" value={entityName} onChange={(event) => setEntityName(event.target.value)} placeholder={m.entityPlaceholder} required />
          <button type="submit" className="homepage-button-primary">{m.show}</button>
        </form>
        {entity ? (entity.labels ? (
          <div className="homepage-muted-panel p-4 text-sm space-y-2">
            <div><strong>{(entity.labels || []).join(', ')}</strong> {JSON.stringify(entity.node)}</div>
            {(entity.links || []).filter((link) => link.type).map((link, index) => (
              <div key={index}>— {link.type} → {(link.labels || []).join(', ')}: {link.fact || link.name}</div>
            ))}
          </div>
        ) : <div className="text-sm text-text-secondary">{m.noResults}</div>) : null}
      </Section>
      ) : null}

      {show('audit') ? (
      <Section id="memory-audit" title={m.auditTitle} subtitle={m.auditSubtitle} count={audit.length}>
        <form className="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); load(); }}>
          <input className="form-input flex-1 min-w-0" value={auditAgent} onChange={(event) => setAuditAgent(event.target.value)} placeholder={m.auditPlaceholder} />
          <button type="submit" className="homepage-button-secondary">{m.filter}</button>
        </form>
        <Table rows={audit.slice(0, auditShown)} empty={m.empty} columns={[
          { key: 'at', label: m.time, render: (row) => formatTime(row.at) },
          { key: 'agent', label: m.agent },
          { key: 'type', label: m.event },
          { key: 'tool', label: m.toolOrModel, render: (row) => row.tool || row.model || '-' },
          { key: 'status', label: m.status },
          { key: 'detail', label: m.detail, render: (row) => [row.tokens ? `${row.tokens} tokens` : '', row.detail || ''].filter(Boolean).join(' · ') || '-' },
        ]} />
        <ShowMore shown={auditShown} total={audit.length} onMore={auditMore} />
      </Section>
      ) : null}

      {show('console') ? (
      <Section id="memory-console" title={m.consoleTitle} subtitle={m.consoleSubtitle}>
        <form className="space-y-2" onSubmit={async (event) => { event.preventDefault(); try { setRows(await requestJson(`${API}/query`, { method: 'POST', body: JSON.stringify({ query: cypher }) })); } catch (failure) { fail(failure); } }}>
          <textarea className="form-input w-full font-mono text-xs" rows={4} value={cypher} onChange={(event) => setCypher(event.target.value)} />
          <button type="submit" className="homepage-button-primary">{m.run}</button>
        </form>
        {rows ? (
          <pre className="homepage-muted-panel p-4 text-xs" style={{ whiteSpace: 'pre-wrap', maxHeight: '420px', overflowY: 'auto' }}>
            {JSON.stringify(rows.rows, null, 2)}{rows.truncated ? `\n… ${m.truncated}` : ''}
          </pre>
        ) : null}
      </Section>
      ) : null}
    </>
  );

  if (embedded) return <div className="admin-page-body">{body}</div>;
  return <main className="site-main admin-dashboard-main admin-page-body">{body}</main>;
};

export default CompanyMemoryPage;
